import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, ArrowRight, Check, CheckCircle2, Copy, Download, FilePlus2, RefreshCw, UploadCloud, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api, { download, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { BRAND_THEME } from "@/lib/constants";
import { Card, PageHeader, Spinner } from "@/components/common";
import { ColumnMapper } from "@/components/ColumnMapper";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = ["Source", "Month", "File", "Match columns", "Check", "Done"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SOURCE_HELP = { A: "Dine-in feedback from Reelo or Zomato", B: "Online reviews from Swiggy or Google (Restaverse)", C: "Online order complaints from Zomato or Swiggy (Restaverse)" };

const Stepper = ({ step }) => (
  <ol className="mb-8 flex items-center gap-2 overflow-x-auto pb-1" data-testid="upload-stepper">
    {STEPS.map((s, i) => {
      const n = i + 1;
      const done = n < step;
      const on = n === step;
      return (
        <li key={s} className="flex shrink-0 items-center gap-2">
          <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${done ? "bg-emerald-500 text-white" : on ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>{done ? <Check className="h-4 w-4" /> : n}</span>
          <span className={`text-sm font-medium ${on ? "text-slate-900" : "text-slate-500"}`}>{s}</span>
          {n < STEPS.length && <span className="mx-1 h-px w-6 bg-slate-300 sm:w-10" />}
        </li>
      );
    })}
  </ol>
);

const Choice = ({ on, onClick, title, text, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`rounded-2xl border-2 p-5 text-left transition-colors ${on ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
    <div className="flex items-center justify-between"><span className="text-base font-semibold text-slate-900">{title}</span>{on && <CheckCircle2 className="h-5 w-5 text-slate-900" />}</div>
    {text && <p className="mt-1 text-sm text-slate-500">{text}</p>}
  </button>
);

const Nav = ({ onBack, onNext, nextLabel = "Continue", disabled, busy, testid = "wizard-next" }) => (
  <div className="mt-8 flex items-center justify-between">
    {onBack ? <Button variant="ghost" className="h-12 rounded-xl" onClick={onBack} data-testid="wizard-back"><ArrowLeft className="mr-2 h-4 w-4" />Back</Button> : <span />}
    {onNext && <Button className="h-12 rounded-xl px-6 text-base" onClick={onNext} disabled={disabled || busy} data-testid={testid}>{busy ? <Spinner /> : <>{nextLabel}<ArrowRight className="ml-2 h-4 w-4" /></>}</Button>}
  </div>
);

const Stat = ({ label, value, tone, icon: Icon, testid }) => (
  <div data-testid={testid} className={`rounded-2xl border p-5 ${tone}`}>
    <div className="flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4" />{label}</div>
    <div className="mt-2 font-display text-3xl font-bold">{value}</div>
  </div>
);

export default function Upload() {
  const { meta, refreshMeta } = useApp();
  const { can } = useAuth();
  const nav = useNavigate();
  const now = new Date();
  const [step, setStep] = useState(1);
  const [src, setSrc] = useState({ source_type: "", platform: "", brand: "" });
  const [period, setPeriod] = useState({ m: now.getMonth() + 1, y: now.getFullYear() });
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [remember, setRemember] = useState(true);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [file, setFile] = useState(null);
  const [outletBrands, setOutletBrands] = useState({});
  const fileRef = useRef(null);
  if (!meta) return null;
  const periodStr = `${period.y}-${String(period.m).padStart(2, "0")}`;

  const reset = () => { setStep(1); setParsed(null); setPreview(null); setResult(null); setMapping({}); };

  const sendFile = async (file, sheet = "") => {
    if (!file) return;
    if (!/\.(xlsx|csv)$/i.test(file.name)) return toast.error("Please choose an Excel (.xlsx) or CSV file.");
    if (file.size > 20 * 1024 * 1024) return toast.error("This file is larger than 20 MB. Please split it.");
    setBusy(true);
    setFile(file);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("sheet", sheet);
    fd.append("source_type", src.source_type);
    fd.append("platform", src.platform);
    fd.append("period", periodStr);
    fd.append("brand", src.brand);
    try {
      const { data } = await api.post("/uploads/parse", fd);
      setParsed({ ...data, file_name: file.name });
      setMapping(data.mapping);
      setStep(4);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/uploads/${parsed.upload_id}/preview`, { mapping, save_template: remember });
      setPreview(data);
      setStep(5);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/uploads/${parsed.upload_id}/commit`);
      setResult(data);
      setStep(6);
      toast.success("Import complete");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const addOutlets = async () => {
    const items = preview.unknown_outlets.map((o) => ({ brand: outletBrands[o.name] || o.brand || src.brand, name: o.name, aliases: [] }));
    if (items.some((i) => !i.brand)) return toast.error("Please choose a brand for each new outlet.");
    try {
      const { data } = await api.post("/outlets/bulk", { items });
      toast.success(`${data.added} outlet${data.added === 1 ? "" : "s"} added`);
      refreshMeta();
      runPreview();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const fields = meta.fields.filter((f) => f.mappable);
  const c = preview?.counts;

  return (
    <div data-testid="upload-page" className="max-w-5xl">
      <PageHeader title="Upload data" subtitle="Add feedback from Reelo, Zomato, Swiggy or Google. We'll guide you step by step." />
      <Stepper step={step} />

      {step === 1 && (
        <Card title="1. What are you uploading?" testid="step-source">
          <div className="grid gap-3 md:grid-cols-3">
            {["A", "B", "C"].map((s) => (
              <Choice key={s} testid={`source-${s}`} on={src.source_type === s} onClick={() => setSrc({ ...src, source_type: s, platform: "" })} title={`${s}. ${meta.source_types[s]}`} text={SOURCE_HELP[s]} />
            ))}
          </div>
          {src.source_type && (
            <div className="mt-6 fade-up">
              <div className="mb-2 text-sm font-semibold text-slate-700">Which platform is the file from?</div>
              <div className="flex flex-wrap gap-2">
                {[...meta.platforms_by_source[src.source_type], "Mixed"].map((p) => (
                  <button key={p} onClick={() => setSrc({ ...src, platform: p })} data-testid={`platform-${p}`} className={`h-12 rounded-xl border-2 px-6 text-base font-semibold ${src.platform === p ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}>{p === "Mixed" ? "Several (read from file)" : p}</button>
                ))}
              </div>
            </div>
          )}
          {src.platform && (
            <div className="mt-6 fade-up">
              <div className="mb-2 text-sm font-semibold text-slate-700">Which brand is it for?</div>
              <div className="flex flex-wrap gap-2">
                {[["", "Several / detect from file"], ...meta.brands.map((b) => [b, b])].map(([v, l]) => (
                  <button key={l} onClick={() => setSrc({ ...src, brand: v })} data-testid={`upload-brand-${v ? v.replace(/[^a-z]/gi, "").toLowerCase() : "detect"}`}
                    className="h-11 rounded-xl border-2 px-4 text-sm font-semibold"
                    style={src.brand === v ? { borderColor: BRAND_THEME[v || "All"].primary, background: BRAND_THEME[v || "All"].bg, color: BRAND_THEME[v || "All"].text } : { borderColor: "#E2E8F0" }}>{l}</button>
                ))}
              </div>
            </div>
          )}
          <Nav onNext={() => setStep(2)} disabled={!src.platform} />
        </Card>
      )}

      {step === 2 && (
        <Card title="2. Which month is this data for?" subtitle="Records are filed by their own date, this just helps us warn you about rows from other months." testid="step-period">
          <div className="flex flex-wrap gap-3">
            <Select value={String(period.m)} onValueChange={(v) => setPeriod({ ...period, m: Number(v) })}>
              <SelectTrigger className="h-12 w-[200px] rounded-xl text-base" data-testid="period-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(period.y)} onValueChange={(v) => setPeriod({ ...period, y: Number(v) })}>
              <SelectTrigger className="h-12 w-[140px] rounded-xl text-base" data-testid="period-year"><SelectValue /></SelectTrigger>
              <SelectContent>{[0, 1, 2, 3].map((d) => <SelectItem key={d} value={String(now.getFullYear() - d)}>{now.getFullYear() - d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Nav onBack={() => setStep(1)} onNext={() => setStep(3)} />
        </Card>
      )}

      {step === 3 && (
        <Card title="3. Add your file" subtitle={`${src.platform} · ${meta.source_types[src.source_type]} · ${MONTHS[period.m - 1]} ${period.y}`} testid="step-file">
          <div
            data-testid="file-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); sendFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-16 text-center transition-colors ${drag ? "border-slate-900 bg-slate-50" : "border-slate-300 hover:border-slate-400"}`}
          >
            {busy ? <Spinner className="h-10 w-10 text-slate-400" /> : <UploadCloud className="h-12 w-12 text-slate-400" />}
            <div className="mt-4 text-lg font-semibold text-slate-800">{busy ? "Reading your file..." : "Drop your file here, or click to choose"}</div>
            <div className="mt-1 text-sm text-slate-500">Excel (.xlsx) or CSV, up to 20 MB</div>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" data-testid="file-input" onChange={(e) => { sendFile(e.target.files[0]); e.target.value = ""; }} />
          </div>
          <button className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:underline" data-testid="download-sample-file"
            onClick={() => download(`/samples/${src.source_type}/${src.platform}`, {}, `sample_${src.source_type}_${src.platform}.xlsx`).catch((e) => toast.error(errMsg(e)))}>
            <Download className="h-4 w-4" />Don't have a file? Download a sample to try
          </button>
          <Nav onBack={() => setStep(2)} />
        </Card>
      )}

      {step === 4 && parsed && (
        <Card title="4. Match your columns" subtitle={`We found ${parsed.total_rows} rows in "${parsed.file_name}". Tell us which column holds what.`} testid="step-mapping">
          {parsed.sheets?.length > 1 && (
            <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 p-3">
              <span className="text-sm font-semibold text-slate-700">This file has several sheets. Read from:</span>
              <Select value={parsed.sheet} onValueChange={(v) => sendFile(file, v)}>
                <SelectTrigger className="h-10 w-[200px] rounded-lg" data-testid="sheet-select"><SelectValue /></SelectTrigger>
                <SelectContent>{parsed.sheets.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {busy && <Spinner />}
            </div>
          )}
          {parsed.template_applied ? (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="template-applied-banner"><Wand2 className="h-4 w-4" />We used your saved matching from last time. Just press Continue.</div>
          ) : (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-indigo-50 px-4 py-3 text-sm text-indigo-800"><Wand2 className="h-4 w-4" />We matched what we could automatically. Please check the ones marked in white.</div>
          )}
          <ColumnMapper fields={fields} columns={parsed.columns} mapping={mapping} onChange={setMapping} sample={parsed.sample} />
          <label className="mt-5 flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-mapping-checkbox" />Remember this matching for next {src.platform} upload</label>
          <Nav onBack={() => setStep(3)} onNext={runPreview} busy={busy} nextLabel="Check the data" testid="run-preview-button" />
        </Card>
      )}

      {step === 5 && preview && (
        <div className="space-y-6" data-testid="step-preview">
          <div className="stagger grid grid-cols-2 gap-4 md:grid-cols-5">
            <Stat testid="preview-total" label="Rows in file" value={c.total} icon={FilePlus2} tone="border-slate-200 bg-white text-slate-800" />
            <Stat testid="preview-new" label="New records" value={c.new} icon={CheckCircle2} tone="border-emerald-200 bg-emerald-50 text-emerald-800" />
            <Stat testid="preview-updated" label="Will be updated" value={c.updated} icon={RefreshCw} tone="border-indigo-200 bg-indigo-50 text-indigo-800" />
            <Stat testid="preview-duplicates" label="Already saved" value={c.duplicates} icon={Copy} tone="border-slate-200 bg-slate-50 text-slate-700" />
            <Stat testid="preview-errors" label="Rows with problems" value={c.errors} icon={AlertCircle} tone="border-red-200 bg-red-50 text-red-800" />
          </div>
          <p className="text-sm text-slate-500">
            "Already saved" rows are skipped, so nothing is ever duplicated. {c.blank > 0 && `${c.blank} empty rows will be ignored. `}
            {c.outside_period > 0 && <span className="font-medium text-amber-700">{c.outside_period} rows are dated outside {MONTHS[period.m - 1]} {period.y} — they'll still be filed under their own month.</span>}
          </p>
          {preview.updates.length > 0 && (
            <Card title="What will be updated" subtitle="Only status details change. Everything else stays as it is." testid="preview-updates">
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs uppercase text-slate-400"><th className="py-2">Record</th><th>Field</th><th>Before</th><th>After</th></tr></thead>
                  <tbody>{preview.updates.flatMap((u) => Object.entries(u.diff).map(([f, [o, n]]) => (
                    <tr key={u.id + f} className="border-b border-slate-100"><td className="py-2 pr-2 text-slate-700">{u.label}</td><td className="pr-2">{meta.labels[f] || f}</td><td className="pr-2 text-slate-400">{o || "empty"}</td><td className="font-medium">{n}</td></tr>
                  )))}</tbody>
                </table>
              </div>
            </Card>
          )}
          {preview.unknown_outlets?.length > 0 && can("admin") && (
            <Card title={`${preview.unknown_outlets.length} outlet name${preview.unknown_outlets.length === 1 ? "" : "s"} we don't know yet`} subtitle="If these are real outlets, add them in one click. If one is just another spelling of an existing outlet, add it as an alias in Settings instead." testid="unknown-outlets-card"
              right={<Button className="rounded-xl" onClick={addOutlets} data-testid="add-unknown-outlets-button">Add as outlets & check again</Button>}>
              <div className="grid gap-2 sm:grid-cols-2">
                {preview.unknown_outlets.map((o) => (
                  <div key={o.name} className="flex items-center justify-between gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm">
                    <span className="min-w-0 truncate font-medium">{o.name} <span className="text-slate-400">({o.rows} rows)</span></span>
                    <Select value={outletBrands[o.name] || o.brand || src.brand || undefined} onValueChange={(v) => setOutletBrands({ ...outletBrands, [o.name]: v })}>
                      <SelectTrigger className="h-9 w-[160px] rounded-lg bg-white"><SelectValue placeholder="Brand..." /></SelectTrigger>
                      <SelectContent>{meta.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {preview.errors.length > 0 && (
            <Card title="Rows we can't import" subtitle="Fix these in your file (or add the outlet name in Settings) and upload again." testid="preview-error-list"
              right={<Button variant="outline" className="rounded-xl" data-testid="download-error-rows" onClick={() => download(`/uploads/${parsed.upload_id}/errors`, {}, "rows_with_problems.csv").catch((e) => toast.error(errMsg(e)))}><Download className="mr-2 h-4 w-4" />Download these rows</Button>}>
              <ul className="max-h-60 space-y-1 overflow-y-auto text-sm">
                {preview.errors.map((e) => <li key={e.row} className="rounded-lg bg-red-50 px-3 py-2 text-red-800"><span className="font-semibold">Row {e.row}:</span> {e.reason}</li>)}
              </ul>
            </Card>
          )}
          {preview.new_sample.length > 0 && (
            <Card title="A peek at the new records" testid="preview-new-sample">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs uppercase text-slate-400"><th className="py-2">Date</th><th>Outlet</th><th>Customer</th><th>Feedback</th><th>Category</th><th>Severity</th></tr></thead>
                  <tbody>{preview.new_sample.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100"><td className="whitespace-nowrap py-2 pr-2">{r.date}</td><td className="whitespace-nowrap pr-2">{r.outlet}</td><td className="whitespace-nowrap pr-2">{r.customer_name}</td><td className="max-w-xs truncate pr-2 text-slate-600">{r.text}</td><td className="pr-2">{r.category}</td><td>{r.severity}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </Card>
          )}
          <Nav onBack={() => setStep(4)} onNext={commit} busy={busy} disabled={!c.new && !c.updated} nextLabel={c.new || c.updated ? `Confirm & import ${c.new + c.updated} records` : "Nothing new to import"} testid="confirm-import-button" />
        </div>
      )}

      {step === 6 && result && (
        <Card testid="step-done" className="text-center">
          <div className="flex flex-col items-center py-8">
            <div className="rounded-full bg-emerald-100 p-4"><CheckCircle2 className="h-10 w-10 text-emerald-600" /></div>
            <h2 className="mt-4 font-display text-2xl font-bold text-slate-900">All done!</h2>
            <p className="mt-2 text-base text-slate-600" data-testid="import-summary">
              {result.counts.new} new records added, {result.counts.updated} updated, {result.counts.skipped} already saved{result.counts.errors ? `, ${result.counts.errors} rows had problems` : ""}.
            </p>
            <p className="mt-1 text-sm text-slate-400">AI is suggesting categories for the new records in the background.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button className="h-12 rounded-xl" onClick={reset} data-testid="upload-another-button">Upload another file</Button>
              <Button variant="outline" className="h-12 rounded-xl" onClick={() => nav("/complaints")} data-testid="go-complaints-button">See complaints</Button>
              <Button variant="outline" className="h-12 rounded-xl" onClick={() => nav("/history")} data-testid="go-history-button">Upload history</Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
